import { useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { DataControls } from "./components/DataControls";
import { GPASummary } from "./components/GPASummary";
import { Container } from "./components/layout/Container";
import { Header } from "./components/layout/Header";
import { QuickGPACalculator } from "./components/QuickGPACalculator";
import { SemesterGrid } from "./components/semester/SemesterGrid";
import { TranscriptButton } from "./components/TranscriptButton";
import { useUniversity } from "./contexts/UniversityContext";
import { useUniversityData } from "./hooks/useUniversityData";
import { Course } from "./types/grades";
import { calculateCumulativeGPA } from "./utils/calculations";

function MainContent({ mode }: { mode: 'semester' | 'quick' }) {
  const { selectedUniversity } = useUniversity();
  const { semesters, setSemesters, isLoading, resetSemesters } = useUniversityData(selectedUniversity);
  const [selectedSemesterId, setSelectedSemesterId] = useState<string>(semesters[0]?.id || "1");
  const [quickCalculatorGPA, setQuickCalculatorGPA] = useState<number | null>(null);

  const cumulativeGPA = calculateCumulativeGPA(semesters, selectedUniversity);
  const selectedSemester = semesters.find((s) => s.id === selectedSemesterId) || semesters[0];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your data...</p>
        </div>
      </div>
    );
  }

  const handleCourseChange = (index: number, updatedCourse: Course) => {
    setSemesters(
      semesters.map((semester) => {
        if (semester.id === selectedSemesterId) {
          const newCourses = [...semester.courses];
          newCourses[index] = updatedCourse;
          return { ...semester, courses: newCourses };
        }
        return semester;
      })
    );
  };

  const addCourse = () => {
    setSemesters(
      semesters.map((semester) => {
        if (semester.id === selectedSemesterId) {
          return {
            ...semester,
            courses: [...semester.courses, { code: "", credits: 0, grade: "" }]
          };
        }
        return semester;
      })
    );
  };

  const deleteCourse = (index: number) => {
    setSemesters(
      semesters.map((semester) => {
        if (semester.id === selectedSemesterId) {
          return {
            ...semester,
            courses: semester.courses.filter((_, i) => i !== index)
          };
        }
        return semester;
      })
    );
  };

  const addSemester = () => {
    const newId = (Math.max(...semesters.map((s) => parseInt(s.id))) + 1).toString();
    setSemesters([
      ...semesters,
      {
        id: newId,
        name: `Semester ${newId}`,
        courses: [{ code: "", credits: 0, grade: "" }],
        gpa: 0
      }
    ]);
    setSelectedSemesterId(newId);
  };

  const removeSemester = (id: string) => {
    if (semesters.length > 1) {
      const newSemesters = semesters.filter((s) => s.id !== id);
      setSemesters(newSemesters);
      if (id === selectedSemesterId) {
        setSelectedSemesterId(newSemesters[0].id);
      }
    }
  };

  return (
    <main className="py-6">
      <Container>
        <div className="space-y-6">
          {mode === "semester" && (
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 sm:justify-between sm:items-center">
              <DataControls onDataImported={resetSemesters} />
              <TranscriptButton semesters={semesters} />
            </div>
          )}

          {mode === "semester" ? (
            <>
              <GPASummary semesters={semesters} />
              <SemesterGrid
                semesters={semesters}
                setSemesters={setSemesters}
                selectedSemesterId={selectedSemesterId}
                selectedSemester={selectedSemester}
                onSelectSemester={setSelectedSemesterId}
                onAddSemester={addSemester}
                onRemoveSemester={removeSemester}
                cumulativeGPA={cumulativeGPA}
                handleCourseChange={handleCourseChange}
                addCourse={addCourse}
                deleteCourse={deleteCourse}
              />
            </>
          ) : (
            <div className="max-w-3xl mx-auto">
              <QuickGPACalculator onCalculate={setQuickCalculatorGPA} />
              {quickCalculatorGPA !== null && (
                <div className="mt-6">
                  <div className="bg-white rounded-lg shadow-md p-6 text-center">
                    <h3 className="text-xl font-bold mb-2">Calculated GPA</h3>
                    <p className="text-4xl font-bold text-blue-600">{quickCalculatorGPA.toFixed(2)}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Container>
    </main>
  );
}

export function App() {
  const [mode, setMode] = useState<"semester" | "quick">("semester");

  return (
    <div className="min-h-screen bg-gray-50">
      <Routes>
        <Route path="/" element={
          <>
            <Header mode={mode} setMode={setMode} />
            <MainContent mode={mode} />
          </>
        } />
        <Route path="/quick" element={
          <>
            <Header mode="quick" setMode={setMode} />
            <MainContent mode="quick" />
          </>
        } />
        <Route path="/semester" element={
          <>
            <Header mode="semester" setMode={setMode} />
            <MainContent mode="semester" />
          </>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <footer className="mt-12 py-6 bg-gray-100">
        <Container>
          <div className="text-center text-gray-600">
            <p>© {new Date().getFullYear()} GPA Calculator</p>
          </div>
        </Container>
      </footer>
    </div>
  );
}